/**
 * FormPreviewSlideOver
 *
 * A right-side slide-over panel that fetches a form schema by ID and
 * renders it using @formio/react for visual preview.
 *
 * Props:
 *   formId   — the form_schemas.id to preview
 *   formName — display name (shown before schema loads)
 *   onClose  — called when the panel should close
 *
 * The panel slides in from the right on mount using a CSS transition.
 * Clicking the backdrop or the ✕ button closes it.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { forms as formsApi } from '../../lib/api.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Props {
  formId:   string;
  formName: string;
  onClose:  () => void;
}

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

// @formio/react Form component shape (lazy-loaded)
type FormioFormComponent = React.ComponentType<{
  form:     unknown;
  options?: Record<string, unknown>;
  onSubmit?: (data: unknown) => void;
}>;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function FormPreviewSlideOver({ formId, formName, onClose }: Props): React.ReactElement {
  const [loadState,  setLoadState]  = useState<LoadState>('loading');
  const [schema,     setSchema]     = useState<unknown>(null);
  const [errorMsg,   setErrorMsg]   = useState<string>('');
  const [FormioForm, setFormioForm] = useState<FormioFormComponent | null>(null);
  const [visible,    setVisible]    = useState(false);    // drives CSS transition

  // ---------------------------------------------------------------------------
  // Slide-in on mount
  // ---------------------------------------------------------------------------

  useEffect(() => {
    // Give the browser one tick to paint the off-screen initial position,
    // then trigger the transition
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  // ---------------------------------------------------------------------------
  // Close with slide-out animation
  // ---------------------------------------------------------------------------

  const handleClose = useCallback(() => {
    setVisible(false);
    // Wait for transition (300ms) before unmounting
    setTimeout(onClose, 300);
  }, [onClose]);

  // ESC key to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleClose]);

  // ---------------------------------------------------------------------------
  // Load form schema + lazy-load @formio/react in parallel
  // ---------------------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;

    const schemaPromise = formsApi.schemas.get(formId).then((res) => {
      if (res.success && res.data?.schemaJson) return res.data.schemaJson;
      throw new Error(res.error ?? 'Failed to load form schema');
    });

    const formioPromise = import('@formio/react').then(
      (mod) => (mod as { Form: FormioFormComponent }).Form,
    );

    Promise.all([schemaPromise, formioPromise])
      .then(([schemaJson, FormComponent]) => {
        if (cancelled) return;
        setSchema(schemaJson);
        setFormioForm(() => FormComponent);
        setLoadState('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setErrorMsg((err as Error).message ?? 'Unknown error');
        setLoadState('error');
      });

    return () => { cancelled = true; };
  }, [formId]);

  // ---------------------------------------------------------------------------
  // Prevent background scroll while open
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          ...styles.backdrop,
          opacity: visible ? 1 : 0,
        }}
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Slide-over panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Preview: ${formName}`}
        style={{
          ...styles.panel,
          transform: visible ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <span style={styles.previewTag}>Preview</span>
            <h2 style={styles.title}>{formName}</h2>
          </div>
          <button
            style={styles.closeBtn}
            onClick={handleClose}
            aria-label="Close preview"
          >
            ✕
          </button>
        </div>

        {/* Notice bar */}
        <div style={styles.noticeBanner}>
          <span style={{ fontSize: 13 }}>👁️</span>
          <span style={styles.noticeText}>
            This is a read-only preview — submissions are disabled.
          </span>
        </div>

        {/* Body */}
        <div style={styles.body}>
          {loadState === 'loading' && (
            <div style={styles.centeredState}>
              <div style={styles.spinner} />
              <p style={styles.stateText}>Loading form…</p>
            </div>
          )}

          {loadState === 'error' && (
            <div style={styles.centeredState}>
              <span style={{ fontSize: 32 }}>⚠️</span>
              <p style={styles.errorText}>{errorMsg}</p>
              <button style={styles.retryBtn} onClick={() => { setLoadState('loading'); }}>
                Retry
              </button>
            </div>
          )}

          {loadState === 'ready' && FormioForm !== null && schema !== null && (
            <div className="ci-formio-preview">
              <FormioForm
                form={schema}
                options={{ readOnly: false }}
                onSubmit={() => {
                  // Preview — swallow submissions silently
                }}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button style={styles.footerCloseBtn} onClick={handleClose}>
            Close Preview
          </button>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position:   'fixed',
    inset:      0,
    background: 'rgba(0,0,0,0.4)',
    zIndex:     1000,
    transition: 'opacity 0.3s ease',
  },
  panel: {
    position:      'fixed',
    top:           0,
    right:         0,
    bottom:        0,
    width:         '600px',
    maxWidth:      '95vw',
    background:    '#fff',
    boxShadow:     '-4px 0 24px rgba(0,0,0,0.12)',
    zIndex:        1001,
    display:       'flex',
    flexDirection: 'column',
    transition:    'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
    overflowY: 'hidden',
  },
  header: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'space-between',
    padding:        '14px 20px',
    borderBottom:   '1px solid #e5e7eb',
    flexShrink:     0,
    background:     '#fff',
  },
  headerLeft: {
    display:    'flex',
    alignItems: 'center',
    gap:        10,
    minWidth:   0,
  },
  previewTag: {
    fontSize:     10,
    fontWeight:   700,
    letterSpacing: 0.8,
    textTransform: 'uppercase' as const,
    color:        '#6b7280',
    background:   '#f3f4f6',
    padding:      '2px 7px',
    borderRadius: 4,
    flexShrink:   0,
  },
  title: {
    margin:       0,
    fontSize:     15,
    fontWeight:   600,
    color:        '#111827',
    whiteSpace:   'nowrap' as const,
    overflow:     'hidden',
    textOverflow: 'ellipsis',
  },
  closeBtn: {
    background:   'none',
    border:       '1px solid #e5e7eb',
    borderRadius: 6,
    width:        32,
    height:       32,
    cursor:       'pointer',
    fontSize:     14,
    color:        '#6b7280',
    flexShrink:   0,
    display:      'flex',
    alignItems:   'center',
    justifyContent: 'center',
  },
  noticeBanner: {
    display:     'flex',
    alignItems:  'center',
    gap:         8,
    background:  '#f0f9ff',
    borderBottom: '1px solid #bae6fd',
    padding:     '7px 20px',
    flexShrink:  0,
  },
  noticeText: {
    fontSize: 12,
    color:    '#0369a1',
  },
  body: {
    flex:      1,
    overflowY: 'auto',
    padding:   '20px 24px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  centeredState: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    justifyContent: 'center',
    minHeight:      240,
    gap:            12,
  },
  spinner: {
    width:           32,
    height:          32,
    border:          '3px solid #e5e7eb',
    borderTopColor:  '#2563eb',
    borderRadius:    '50%',
    animation:       'spin 0.8s linear infinite',
  },
  stateText: { fontSize: 14, color: '#9ca3af', margin: 0 },
  errorText:  { fontSize: 14, color: '#b91c1c', margin: 0, textAlign: 'center' as const, maxWidth: 320 },
  retryBtn:  {
    padding:      '7px 16px',
    background:   '#2563eb',
    color:        '#fff',
    border:       'none',
    borderRadius: 6,
    cursor:       'pointer',
    fontSize:     13,
    fontWeight:   500,
  },
  footer: {
    padding:      '12px 20px',
    borderTop:    '1px solid #e5e7eb',
    flexShrink:   0,
    display:      'flex',
    justifyContent: 'flex-end',
    background:   '#fafafa',
  },
  footerCloseBtn: {
    padding:      '7px 18px',
    background:   '#f9fafb',
    border:       '1px solid #d1d5db',
    borderRadius: 6,
    cursor:       'pointer',
    fontSize:     13,
    color:        '#374151',
    fontWeight:   500,
  },
};
